import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Button from "../common/Button";
import { verifyProfileOtp, resendProfileOtp } from "../../services/accountService";
import { color, radius } from "../../styles/theme";

const OTP_LENGTH = 4;
const RESEND_SECONDS = 60;

/**
 * Inline phone-verification panel, shared by Profile and Business Details
 * tabs. Reimplements just the profile-update branch of mobile's shared
 * OTPVerification screen (src/Screens/OTPVerification) — that screen also
 * handles signup/login/forgot-password, none of which apply here — as an
 * inline panel instead of a route, matching the pattern already used for the
 * Create Event production OTP gate (CreateEvent.jsx).
 *
 * @param {{ userId: string|number, email: string, phoneNumber: string, onVerified: () => void, onCancel: () => void }} props
 */
const OtpVerifyPanel = ({ userId, email, phoneNumber, onVerified, onCancel }) => {
  const { t } = useTranslation(["settings", "common"]);
  const [otp, setOtp] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resendIn, setResendIn] = useState(RESEND_SECONDS);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const handleVerify = async () => {
    if (otp.trim().length !== OTP_LENGTH) {
      toast.error(t("otpVerifyPanel.toasts.enterDigitCode", { length: OTP_LENGTH }));
      return;
    }
    setVerifying(true);
    try {
      await verifyProfileOtp(otp.trim(), userId);
      toast.success(t("otpVerifyPanel.toasts.phoneVerified"));
      onVerified();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("otpVerifyPanel.toasts.invalidOrExpiredCode"));
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await resendProfileOtp(email, phoneNumber);
      setResendIn(RESEND_SECONDS);
      toast.info(t("otpVerifyPanel.toasts.codeResent"));
    } catch {
      toast.error(t("otpVerifyPanel.toasts.resendFailed"));
    } finally {
      setResending(false);
    }
  };

  return (
    <div style={s.box}>
      <label style={s.label}>{t("otpVerifyPanel.label")}</label>
      <input
        type="text"
        inputMode="numeric"
        maxLength={OTP_LENGTH}
        className="input"
        style={s.input}
        value={otp}
        onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
        placeholder={t("otpVerifyPanel.codePlaceholder", { length: OTP_LENGTH })}
        autoFocus
      />
      <p style={s.hint}>
        {t("otpVerifyPanel.codeSentTo", { phone: phoneNumber || t("otpVerifyPanel.yourPhone") })}{" "}
        {resendIn > 0 ? (
          <span>{t("otpVerifyPanel.resendIn", { seconds: resendIn })}</span>
        ) : (
          <button type="button" className="btn btn-ghost btn-sm" style={s.linkBtn} onClick={handleResend} disabled={resending}>
            {resending ? t("otpVerifyPanel.sending") : t("otpVerifyPanel.resend")}
          </button>
        )}
      </p>
      <div style={s.actions}>
        <Button onClick={handleVerify} disabled={verifying}>
          {verifying ? t("otpVerifyPanel.verifying") : t("otpVerifyPanel.verify")}
        </Button>
        <Button variant="secondary" onClick={onCancel} disabled={verifying}>
          {t("common:actions.cancel")}
        </Button>
      </div>
    </div>
  );
};

const s = {
  box: { marginTop: 8, marginBottom: 16, padding: 18, borderRadius: radius.md, background: color.primary50, border: `1.5px solid ${color.primary100}` },
  label: { display: "block", marginBottom: 6, fontWeight: 600, fontSize: 13, color: color.gray700 },
  input: { width: 160, fontSize: 16, letterSpacing: 4 },
  hint: { fontSize: 12, color: color.textSecondary, marginTop: 10 },
  linkBtn: { color: color.primary600, display: "inline-flex", padding: "2px 6px" },
  actions: { display: "flex", gap: 10, marginTop: 14 },
};

export default OtpVerifyPanel;
